import * as Sentry from '@sentry/react';
import {
  type InfiniteData,
  type Query,
  type UseInfiniteQueryOptions,
  type UseQueryOptions,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';
import type { ImageView } from '@vizit/api';
import { SENTRY_FEATURE_TAGS, SENTRY_STAGE_TAGS } from '@vizit/constants';
import {
  type ApiRoutersPortfoliosPdpsPdpsImageViewFields,
  type SearchPdpImagesV1PortfoliosPortfolioIdPdpsPdpIdImagesGetData,
  type SearchPdpImagesV1PortfoliosPortfolioIdPdpsPdpIdImagesGetError,
  type SearchPdpImagesV1PortfoliosPortfolioIdPdpsPdpIdImagesGetResponse,
  searchPdpImagesV1PortfoliosPortfolioIdPdpsPdpIdImagesGet,
} from '../../generated';

export type SearchPdpImagesQueryVariables = SearchPdpImagesV1PortfoliosPortfolioIdPdpsPdpIdImagesGetData['path'] &
  SearchPdpImagesV1PortfoliosPortfolioIdPdpsPdpIdImagesGetData['query'];

export type SearchPdpImagesQuery = {
  data: SearchPdpImagesV1PortfoliosPortfolioIdPdpsPdpIdImagesGetResponse;
  total_count: number;
};

/**
 * Extracts the total_count from the Vizit-Total-Count response header
 */
function extractTotalCount(response: Response): number {
  const totalCountHeader = response.headers.get('vizit-total-count');
  return totalCountHeader ? Number.parseInt(totalCountHeader, 10) : 0;
}

export const useSearchPdpImagesQuery = <
  TData = SearchPdpImagesQuery,
  TError = SearchPdpImagesV1PortfoliosPortfolioIdPdpsPdpIdImagesGetError,
>(
  {
    portfolio_id,
    pdp_id,
    image_type,
    limit,
    offset,
    sort_by,
    sort_direction,
    source,
    exclude_source,
  }: SearchPdpImagesQueryVariables,
  options?: Omit<UseQueryOptions<SearchPdpImagesQuery, TError, TData>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<SearchPdpImagesQuery, TError, TData>({
    queryKey: [
      'SearchPdpImages',
      { portfolio_id, pdp_id, image_type, limit, offset, sort_by, sort_direction, source, exclude_source },
    ],
    queryFn: async () => {
      const { data, response } = await searchPdpImagesV1PortfoliosPortfolioIdPdpsPdpIdImagesGet({
        path: { portfolio_id, pdp_id },
        query: {
          image_type,
          limit,
          offset,
          sort_by: sort_by as ApiRoutersPortfoliosPdpsPdpsImageViewFields,
          sort_direction,
          source,
          exclude_source,
        },
        throwOnError: true,
      });
      return {
        data: data ?? [],
        total_count: extractTotalCount(response),
      };
    },
    refetchInterval: (query: Query<SearchPdpImagesQuery, TError>) => {
      const maxRetries = typeof options?.retry === 'number' ? options.retry : 100;
      const maxRetriesReached = query.state.dataUpdateCount >= maxRetries;

      // If any image does not have a vizit score or url, refetch the query.
      const imagesDoNotHaveVizitScore = query.state.data?.data.some((image) => image.vizit_score === null);
      const imagesDoNotHaveUrl = query.state.data?.data.some(
        (image) => image.url === null || !image.url.includes('/images'),
      );

      if ((imagesDoNotHaveVizitScore || imagesDoNotHaveUrl) && !maxRetriesReached) {
        return 1000 * (query.state.dataUpdateCount + 1);
      }

      if ((imagesDoNotHaveVizitScore || imagesDoNotHaveUrl) && maxRetriesReached) {
        Sentry.withScope((scope) => {
          scope.setFingerprint(['GET', '/v1/portfolios/{portfolio_id}/pdps/{pdp_id}/images', 'polling_timeout']);

          Sentry.captureException(new Error('Max retries reached for search pdp images query.'), {
            level: 'warning',
            tags: {
              feature: SENTRY_FEATURE_TAGS.Product,
              stage: SENTRY_STAGE_TAGS[SENTRY_FEATURE_TAGS.Product].productAssetsPolling,
            },
            extra: {
              portfolio_id,
              pdp_id,
              retry_count: query.state.dataUpdateCount,
              max_count: maxRetries,
              query_variables: {
                portfolio_id,
                pdp_id,
                image_type,
                limit,
                offset,
                sort_by,
                sort_direction,
                source,
                exclude_source,
              },
              missing_scores: imagesDoNotHaveVizitScore,
              missing_urls: imagesDoNotHaveUrl,
            },
          });
        });
      }

      return false;
    },
    refetchIntervalInBackground: true,
    ...options,
  });
};

useSearchPdpImagesQuery.getKey = ({
  portfolio_id,
  pdp_id,
  image_type,
  limit,
  offset,
  sort_by,
  sort_direction,
  source,
  exclude_source,
}: SearchPdpImagesQueryVariables) => [
  'SearchPdpImages',
  { portfolio_id, pdp_id, image_type, limit, offset, sort_by, sort_direction, source, exclude_source },
];

useSearchPdpImagesQuery.fetcher = (
  {
    portfolio_id,
    pdp_id,
    image_type,
    limit,
    offset,
    sort_by,
    sort_direction,
    source,
    exclude_source,
  }: SearchPdpImagesQueryVariables,
  _headers?: RequestInit['headers'],
) => {
  return async () => {
    const { data, response } = await searchPdpImagesV1PortfoliosPortfolioIdPdpsPdpIdImagesGet({
      path: { portfolio_id, pdp_id },
      query: {
        image_type,
        limit,
        offset,
        sort_by: sort_by as ApiRoutersPortfoliosPdpsPdpsImageViewFields,
        sort_direction,
        source,
        exclude_source,
      },
      throwOnError: true,
    });
    return {
      data: data ?? [],
      total_count: extractTotalCount(response),
    };
  };
};

export type SearchPdpImagesInfiniteQueryVariables = Omit<SearchPdpImagesQueryVariables, 'offset'>;

export const useSearchPdpImagesInfiniteQuery = <
  TData = SearchPdpImagesQuery,
  TError = SearchPdpImagesV1PortfoliosPortfolioIdPdpsPdpIdImagesGetError,
>(
  {
    portfolio_id,
    pdp_id,
    image_type,
    limit,
    sort_by,
    sort_direction,
    source,
    exclude_source,
  }: SearchPdpImagesInfiniteQueryVariables,
  options?: Omit<
    UseInfiniteQueryOptions<SearchPdpImagesQuery, TError, TData>,
    'queryKey' | 'queryFn' | 'getNextPageParam' | 'initialPageParam'
  >,
) => {
  return useInfiniteQuery<SearchPdpImagesQuery, TError, TData>({
    queryKey: [
      'SearchPdpImagesInfinite',
      { portfolio_id, pdp_id, image_type, limit, sort_by, sort_direction, source, exclude_source },
    ],
    queryFn: async ({ pageParam = 0 }) => {
      const { data, response } = await searchPdpImagesV1PortfoliosPortfolioIdPdpsPdpIdImagesGet({
        path: { portfolio_id, pdp_id },
        query: {
          image_type,
          limit,
          offset: pageParam as number,
          sort_by: sort_by as ApiRoutersPortfoliosPdpsPdpsImageViewFields,
          sort_direction,
          source,
          exclude_source,
        },
        throwOnError: true,
      });
      return {
        data: data ?? [],
        total_count: extractTotalCount(response),
      };
    },
    getNextPageParam: (lastPage, allPages) => {
      const pageSize = limit || 12; // Default to 12 if no limit specified
      const currentOffset = (allPages.length - 1) * pageSize;
      const nextOffset = currentOffset + pageSize;

      // Check if we have more data to fetch
      if (lastPage.data.length < pageSize || nextOffset >= lastPage.total_count) {
        return undefined; // No more pages
      }

      return nextOffset;
    },
    getPreviousPageParam: (_, allPages) => {
      // Return undefined when we're on the first/only page (initial load)
      // Return a value when we have multiple pages (subsequent loads)
      return allPages.length <= 1 ? undefined : 0;
    },
    initialPageParam: 0,
    refetchInterval: (query: Query<SearchPdpImagesQuery, TError, InfiniteData<SearchPdpImagesQuery, unknown>>) => {
      const maxRetries = typeof options?.retry === 'number' ? options.retry : 100;
      const maxRetriesReached = query.state.dataUpdateCount >= maxRetries;

      // Check all pages for images without scores or URLs
      const allImages = query.state.data?.pages?.flatMap((page: SearchPdpImagesQuery) => page.data) || [];
      const imagesDoNotHaveVizitScore = allImages.some((image: ImageView) => image.vizit_score === null);
      const imagesDoNotHaveUrl = allImages.some(
        (image: ImageView) => image.url === null || !image.url.includes('/images'),
      );

      if ((imagesDoNotHaveVizitScore || imagesDoNotHaveUrl) && !maxRetriesReached) {
        return 1000 * (query.state.dataUpdateCount + 1);
      }

      if ((imagesDoNotHaveVizitScore || imagesDoNotHaveUrl) && maxRetriesReached) {
        Sentry.withScope((scope) => {
          scope.setFingerprint(['GET', '/v1/portfolios/{portfolio_id}/pdps/{pdp_id}/images', 'polling_timeout']);

          Sentry.captureException(new Error('Max retries reached for search pdp images infinite query.'), {
            level: 'warning',
            tags: {
              feature: SENTRY_FEATURE_TAGS.Product,
              stage: SENTRY_STAGE_TAGS[SENTRY_FEATURE_TAGS.Product].productAssetsPolling,
            },
            extra: {
              portfolio_id,
              pdp_id,
              query_variables: {
                portfolio_id,
                pdp_id,
                image_type,
                limit,
                sort_by,
                sort_direction,
                source,
                exclude_source,
              },
              retry_count: query.state.dataUpdateCount,
              max_count: maxRetries,
              missing_scores: imagesDoNotHaveVizitScore,
              missing_urls: imagesDoNotHaveUrl,
            },
          });
        });
      }

      return false;
    },
    refetchIntervalInBackground: true,
    ...options,
  });
};

useSearchPdpImagesInfiniteQuery.getKey = ({
  portfolio_id,
  pdp_id,
  image_type,
  limit,
  sort_by,
  sort_direction,
  source,
  exclude_source,
}: SearchPdpImagesInfiniteQueryVariables) => [
  'SearchPdpImagesInfinite',
  { portfolio_id, pdp_id, image_type, limit, sort_by, sort_direction, source, exclude_source },
];

useSearchPdpImagesInfiniteQuery.fetcher = (
  {
    portfolio_id,
    pdp_id,
    image_type,
    limit,
    sort_by,
    sort_direction,
    source,
    exclude_source,
  }: SearchPdpImagesInfiniteQueryVariables,
  offset = 0,
  _headers?: RequestInit['headers'],
) => {
  return async () => {
    const { data, response } = await searchPdpImagesV1PortfoliosPortfolioIdPdpsPdpIdImagesGet({
      path: { portfolio_id, pdp_id },
      query: {
        image_type,
        limit,
        offset,
        sort_by: sort_by as ApiRoutersPortfoliosPdpsPdpsImageViewFields,
        sort_direction,
        source,
        exclude_source,
      },
      throwOnError: true,
    });
    return {
      data: data ?? [],
      total_count: extractTotalCount(response),
    };
  };
};
